import { Loading } from '@nike/frame-component-library';
import {
  match, Default, reduceEntry, isSome,
} from '@nike/rcf-fp';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';

import { deleteReason, getReasons } from '../../utils/service-calls/posreasons';
import { companyIdMappings, scopeLevelMappings } from '../../utils/static/pos-reasons-mappings';
import {
  Table, ButtonBlack, ButtonRed, Select, SelectMultiple,
} from '../reusable';

import ReasonForm from './ReasonForm';

const SUCCESS_STATUS_CODE = 200;

const COMPANY_NAME_BY_ID = companyIdMappings
  .map(({ label, value }) => [value, label])
  .reduce(reduceEntry);

const isNotBlank = (s) => isSome(s) && s !== '';

const ViewReason = ({
  userToken, companyId,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [reasonData, setReasonData] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [editingReason, setEditingReason] = useState();
  const [filterReasonType, setFilterReasonType] = useState([]);
  const [filterScopeLevel, setFilterScopeLevel] = useState('ALL');
  const [filterSearch, setFilterSearch] = useState('');

  const fetchReasons = useCallback(() => {
    setFetchError('');
    setIsLoading(true);
    return getReasons(userToken, companyId)
      .then(({ body }) => setReasonData(body))
      .catch((err) => setFetchError(err.message))
      .finally(() => setIsLoading(false));
  }, [userToken, companyId]);

  useEffect(() => {
    setFilterReasonType([]);
    setFilterScopeLevel('ALL');
    setFilterSearch('');
  }, [companyId]);

  const deleteTheReason = ({ id }) => {
    setErrorMessage('');
    setSuccessMessage('');
    return deleteReason(userToken, companyId, id)
      .catch((err) => {
        setErrorMessage(err.message);
      })
      .then((data) => {
        setErrorMessage(data?.statusCode === SUCCESS_STATUS_CODE ? '' : (data?.body || `Failed to delete ${id}`));
        setSuccessMessage(data?.statusCode === SUCCESS_STATUS_CODE ? `Successfully Deleted ${id}` : '');
        setIsLoading(true);
        return fetchReasons();
      });
  };

  useEffect(() => {
    fetchReasons();
  }, [fetchReasons]);

  const selectedReasonTypes = filterReasonType.map((opt) => opt.value);
  const filteredData = reasonData.filter((reason) => {
    if (selectedReasonTypes.length && !selectedReasonTypes.includes(reason.reasonType)) return false;
    if (filterScopeLevel !== 'ALL' && reason.scopeLevel !== filterScopeLevel) return false;
    if (filterSearch) {
      const term = filterSearch.toLowerCase();
      const searchable = [reason.code, reason.partnerCode, reason.context]
        .filter(Boolean).join(' ').toLowerCase();
      if (!searchable.includes(term)) return false;
    }
    return true;
  });

  const reasonTypeOptions = [...new Set(reasonData.map((r) => r.reasonType).filter(isNotBlank))]
    .sort()
    .map((type) => ({ label: type, value: type }));

  const scopeLevelOptions = [{ label: 'All', value: 'ALL' }, ...scopeLevelMappings];

  const getColumns = () => {
    const columns = [
      {
        accessor: 'reasonType',
        Header: 'Type',
      },
      {
        accessor: 'code',
        Header: 'Code',
      },
      {
        accessor: 'partnerCode',
        Header: 'Partner Code',
      },
      {
        accessor: 'context',
        Header: 'Context',
      },
      {
        accessor: 'scopeLevel',
        Header: 'Scope Level',
      },
      {
        accessor: ({ scopeLevel, scopeValue }) => {
          const isValidCompanyScopeValue = isNotBlank(scopeLevel)
                        && isNotBlank(scopeValue)
                        && scopeLevel === 'COMPANY'
                        && Object.keys(COMPANY_NAME_BY_ID).includes(scopeValue);
          return isValidCompanyScopeValue
            ? COMPANY_NAME_BY_ID[scopeValue]
            : scopeValue;
        },
        Header: 'Scope Value',
        id: 'scopeValue',
      },
      {
        Cell: (d) => <ButtonBlack className="" label="Edit" onClick={() => setEditingReason(d.original)} />,
        Header: 'Edit',
        width: 80,
      },
      {
        Cell: (d) => <ButtonRed className="" confirmationMessage="Do you really want to permanently delete this POS reason?" label={<i className="g72-x-thick" />} onClick={() => deleteTheReason(d.original)} />,
        Header: 'Delete',
        width: 80,
      },
    ];
    return columns;
  };

  return match()(
    [() => isLoading, <Loading />],
    [() => editingReason, (
      <ReasonForm
        isEditing
        closeReasonForm={() => {
          setEditingReason(false);
          fetchReasons();
        }}
        companyId={companyId}
        reason={editingReason}
        userToken={userToken}
      />
    )],
    [Default, (
      <article className="ncss-col-sm-10 ta-sm-l"> {
          fetchError
            ? <section>{fetchError}</section>
            : (
              <section>
                {successMessage && (
                  <p className="text-color-success ta-sm-c body-2 mt2-sm">
                    {successMessage}
                  </p>
                )}
                {errorMessage && (
                  <p className="text-color-error ta-sm-c body-2 mt2-sm">
                    {errorMessage}
                  </p>
                )}
                <div
                  className="ncss-row mb4-sm mt4-sm"
                  style={{
                    alignItems: 'flex-end',
                    display: 'flex',
                    gap: '16px',
                  }}
                >
                  <SelectMultiple
                    id="filter-reason-type"
                    label="Reason Type"
                    options={reasonTypeOptions}
                    values={filterReasonType}
                    width={4}
                    zIndex={20}
                    onChange={setFilterReasonType}
                  />
                  <Select
                    className="ncss-col-sm-3 ta-sm-l va-sm-t"
                    id="filter-scope-level"
                    label="Scope Level"
                    options={scopeLevelOptions}
                    value={filterScopeLevel}
                    zIndex={19}
                    onChange={setFilterScopeLevel}
                  />
                  <article className="ncss-col-sm-4 va-sm-t ta-sm-l">
                    <label htmlFor="filter-search">Search</label>
                    <input
                      className="ncss-input pt2-sm pr4-sm pb2-sm pl4-sm"
                      id="filter-search"
                      placeholder="Search by code, partner code, or context..."
                      style={{
                        border: '1px solid lightgrey',
                        borderRadius: '4px',
                        height: '38px',
                        width: '100%',
                      }}
                      type="text"
                      value={filterSearch}
                      onChange={(e) => setFilterSearch(e.target.value)}
                    />
                  </article>
                </div>
                <p className="body-4 mb2-sm" style={{ color: '#757575' }}>
                  {`Showing ${filteredData.length} of ${reasonData.length} reasons`}
                </p>
                <Table
                  fetched
                  columns={getColumns()}
                  data={filteredData}
                  defaultPageSize={20}
                />
              </section>
            )
        }
      </article>
    )],
  );
};

ViewReason.propTypes = {
  companyId: PropTypes.string.isRequired,
  userToken: PropTypes.string.isRequired,
};

export default ViewReason;
